import * as R from 'ramda';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { CSS } from '@dnd-kit/utilities';
import React, { forwardRef } from 'react';
import { restrictToHorizontalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import {
  useSensor,
  DndContext,
  useSensors,
  MouseSensor,
  TouchSensor,
  closestCenter,
  KeyboardSensor,
} from '@dnd-kit/core';
import {
  useSortable,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// icons
import * as I from '../../../svgs';
// ui
import { Box, Flex, RelativeFlex, MainActionButton } from '../../../ui';
//////////////////////////////////////////////////

const darkColor = G.getTheme('colors.black');
const tabColor = G.getTheme('colors.#F8FAFD');
const borderGrayColor = G.getTheme('colors.borderGray');
const mainDarkColor = G.getTheme('colors.dark.mainDark');
const blueGreyColor = G.getTheme('colors.light.blueGrey');
const dndModifiers = [restrictToHorizontalAxis, restrictToParentElement];

const getPinnedReportId = (report) => `pinned-report-dnd-${G.getGuidFromObject(report)}`;

const PinnedReport = (props) => {
  const {
    guid,
    name,
    type,
    isActive,
    pinnedReport,
    defaultReport,
    handleSelectReport,
    handlePinOrUnpinReport,
    changeDefaultReportRequest,
  } = props;

  const borderRadius = G.ifElse(isActive, '10px 10px 0px 0px', 10);

  return (
    <Tab
      label={name}
      component={forwardRef((_, ref) => (
        <Box
          ref={ref}
          borderRadius={borderRadius}
          bg={G.ifElse(isActive, tabColor, 'initial')}
          border={G.ifElse(isActive, `1px solid ${tabColor}`, 'none')}
        >
          <Flex px='3px'>
            <Box
              p='5px 3px'
              bg={tabColor}
              fontSize={12}
              cursor='pointer'
              borderRadius={borderRadius}
              onClick={() => handleSelectReport(guid)}
              fontWeight={G.ifElse(isActive, 'bold', 'normal')}
              color={G.ifElse(isActive, darkColor, blueGreyColor)}
            >
              {name}
            </Box>
            {
              isActive &&
              <Flex ml={10}>
                <Flex
                  p='0 3px'
                  cursor='pointer'
                  onClick={() => changeDefaultReportRequest({ reportGuid: guid, reportType: type })}
                >
                  {I.pinNew(G.getThemeByCond(defaultReport, 'colors.black', 'colors.dark.grey'), 8, 10)}
                </Flex>
                <Flex
                  p='3px'
                  cursor='pointer'
                  onClick={() => handlePinOrUnpinReport({ pinnedReport, reportGuid: guid })}
                >
                  {I.closeIcon(darkColor, 8)}
                </Flex>
              </Flex>
            }
          </Flex>
        </Box>
      ))}
    />
  );
};

const SortablePinnedReport = (props) => {
  const {
    isActive,
    reportData,
    handleSelectReport,
    handlePinOrUnpinReport,
    changeDefaultReportRequest,
  } = props;

  const sortableId = getPinnedReportId(reportData);

  const {
    listeners,
    transform,
    attributes,
    setNodeRef,
    transition,
    isDragging,
  } = useSortable({
    id: sortableId,
    animateLayoutChanges: () => false,
  });

  const style = {
    transition,
    zIndex: isDragging ? 1 : 0,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
    transform: CSS.Translate.toString(transform),
  };

  return (
    <Box
      {...attributes}
      {...listeners}
      style={style}
      ref={setNodeRef}
    >
      <PinnedReport
        {...reportData}
        isActive={isActive}
        handleSelectReport={handleSelectReport}
        handlePinOrUnpinReport={handlePinOrUnpinReport}
        changeDefaultReportRequest={changeDefaultReportRequest}
      />
    </Box>
  );
};

export const PinnedReports = (props) => {
  const {
    list,
    selectedReport,
    useTableUiWithBg,
    handleSelectReport,
    handlePinOrUnpinReport,
    pinnedReportsPanelActions,
    changeDefaultReportRequest,
  } = props;

  const guid = G.getGuidFromObject(selectedReport);
  const value = R.indexOf(R.find(R.propEq(guid, GC.FIELD_GUID), list), list);
  const validValue = G.ifElse(R.equals(value, -1), false, value);
  const sortableIds = R.map(getPinnedReportId, list);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = ({ active, over }) => {
    const activeId = R.prop('id', active);
    const overId = R.prop('id', over);

    if (R.or(R.isNil(overId), R.equals(activeId, overId))) return;

    const startIndex = R.indexOf(activeId, sortableIds);
    const endIndex = R.indexOf(overId, sortableIds);

    if (R.or(R.equals(startIndex, -1), R.equals(endIndex, -1))) return;

    const newList = G.reorderList(list, startIndex, endIndex);

    handlePinOrUnpinReport(R.map(G.getGuidFromObject, newList));
  };

  return (
    <DndContext
      sensors={sensors}
      onDragEnd={onDragEnd}
      modifiers={dndModifiers}
      collisionDetection={closestCenter}
    >
      <RelativeFlex
        pt='6px'
        zIndex='0'
        justifyContent='space-between'
        bg={G.getTheme('colors.white')}
        px={G.ifElse(useTableUiWithBg, '5px', 10)}
        borderColor={G.getTheme('colors.lightGrey')}
      >
        {
          G.isNotNilAndNotEmpty(list) &&
          <SortableContext items={sortableIds} strategy={horizontalListSortingStrategy}>
            <Tabs
              value={validValue}
              variant='scrollable'
              scrollButtons='auto'
              indicatorColor='primary'
              style={{ minHeight: '26px' }}
              TabIndicatorProps={{
                sx: {
                  height: 3,
                  backgroundColor: tabColor,
                },
              }}
            >
              {
                R.map((reportData) => {
                  const reportGuid = G.getGuidFromObject(reportData);
                  const isActive = R.equals(guid, reportGuid);

                  return (
                    <SortablePinnedReport
                      key={reportGuid}
                      isActive={isActive}
                      reportData={reportData}
                      handleSelectReport={handleSelectReport}
                      handlePinOrUnpinReport={handlePinOrUnpinReport}
                      changeDefaultReportRequest={changeDefaultReportRequest}
                    />
                  );
                }, list)
              }
            </Tabs>
          </SortableContext>
        }
        {
          G.isNotNilAndNotEmpty(pinnedReportsPanelActions) &&
          <Flex
            px={10}
            gap={12}
            mb='1px'
            ml='auto'
            height={25}
            fontSize={13}
            flexShrink='0'
          >
            {G.mapIndexed((button, key) => {
              const { title, action, component } = button;

              if (G.isNotNilAndNotEmpty(component)) return component;

              return (
                <MainActionButton
                  key={key}
                  height='auto'
                  fontSize={13}
                  onClick={action}
                  fontWeight={600}
                  borderRadius={10}
                  border='1px solid'
                  color={mainDarkColor}
                  borderColor={borderGrayColor}
                >
                  {title}
                </MainActionButton>
              );
            }, pinnedReportsPanelActions)}
          </Flex>
        }
      </RelativeFlex>
    </DndContext>
  );
};
